import type { ClientRecord, CompletenessIssue, RecordFindings, RulesConfig } from './types';
import { classify } from './rules';

/**
 * Detects regulatory findings on a record:
 *
 *  - discrepancy: stored risk_classification disagrees with what the rules say
 *    (per FCA SYSC / MLR 2017: a record whose classification contradicts the
 *    recorded data is a material compliance failure, not a data-quality issue)
 *  - incomplete: required fields missing for the file-review checklist
 */
export function evaluateRecord(record: ClientRecord, rules: RulesConfig): RecordFindings {
  const computed = classify(record, rules);
  const incomplete: CompletenessIssue[] = [];

  if (!record.relationship_manager?.trim()) {
    incomplete.push({
      field: 'relationship_manager',
      message: 'No RM attributed — record is not attributable',
    });
  }
  if (record.kyc_status === 'APPROVED' && !record.id_verification_date) {
    incomplete.push({
      field: 'id_verification_date',
      message: 'KYC APPROVED without ID verification date',
    });
  }
  if (computed.tier === 'HIGH' && record.kyc_status === 'APPROVED') {
    incomplete.push({
      field: 'kyc_status',
      message: 'HIGH-risk client APPROVED without EDD — requires senior compliance sign-off',
    });
  }
  if (!record.documentation_complete && record.kyc_status === 'APPROVED') {
    incomplete.push({
      field: 'documentation_complete',
      message: 'APPROVED but documentation marked incomplete',
    });
  }
  if (!record.risk_classification) {
    incomplete.push({
      field: 'risk_classification',
      message: 'No risk classification recorded',
    });
  }

  const discrepancy =
    record.risk_classification !== '' && record.risk_classification !== computed.tier;

  return {
    computed,
    storedTier: record.risk_classification,
    discrepancy,
    incomplete,
  };
}
